/** Reading an organisation's logo as pixels: the browser-tab favicon and the sampled brand
 * colours (POS2-1149).
 *
 * **Why a `data:` URI and not a link to the logo.** The only URL the app ever has for a logo is a
 * presigned R2 read URL that expires in an hour (`r2_images.py`, `READ_URL_TTL_SECONDS`). A favicon
 * is fetched by the *browser*, outside the app's auth and on its own schedule, so a presigned href
 * would 403 once expired — and because the URL changes every session it would churn a fresh entry
 * in the browser's favicon cache rather than ever reusing one. Rendering the bytes to a canvas and
 * handing over a `data:` URI removes the URL, and with it both problems, at the cost of one draw.
 *
 * **Why the canvas read is allowed.** `backend/app/core/r2_cors.py` permits `GET` from every
 * frontend origin, so an image loaded with `crossOrigin = "anonymous"` leaves the canvas untainted
 * and `getImageData`/`toDataURL` both work. That is a fact about the buckets, not about the browser
 * — and it is not true of every bucket, which is why every entry point here tolerates a tainted
 * canvas instead of assuming one it can read.
 *
 * Every entry point here returns null or an empty list rather than throwing. The caller is
 * decorating the app with an organisation's mark; nothing here is worth a broken page.
 */

import { hexToOklch } from "@/lib/brand";

/** 64px covers the 16, 32 and 48px sizes browsers actually paint, and costs a few KiB as a
 * `data:` URI. Larger buys nothing a tab strip can show. */
const FAVICON_SIZE = 64;

/** The sampler needs enough pixels to find the minority colours in a mark, not a faithful image.
 * 48² is 2,304 samples, which is ample against the 4,096 buckets below. */
const SAMPLE_SIZE = 48;

/** Alpha below this is padding or an antialiased edge, and an edge pixel is a blend of the mark
 * and whatever is behind it — so it names a colour the logo does not contain. */
const MIN_ALPHA = 128;

/** Thresholds measured against real brand colours rather than chosen (POS2-1149).
 *
 * OKLCH chroma splits the two sets cleanly: greys sit at 0.000, and the near-greys that read as
 * "no colour chosen" cluster just under 0.05 — muted sage `#9ca3af` at 0.019, near-white cream
 * `#fdf6e3` at 0.026, dark navy `#111827` at 0.032, `PLATFORM_DEFAULT_COLOR` itself at 0.040,
 * washed denim `#64748b` at 0.041. The dimmest thing that still reads as a deliberate colour is
 * deep teal `#0f766e` at 0.086, and the ordinary brand palette runs 0.16–0.25. So 0.05 rejects
 * exactly the greys and accepts exactly the colours.
 *
 * The lightness window is what stops a technically-saturated colour that paints as black or white:
 * below 0.20 a button reads black whatever its hue, above 0.90 it reads white. Both bounds land
 * outside real cases — very dark green `#052e16` is 0.266 and pale pink `#fbcfe8` is 0.899.
 */
const MIN_CHROMA = 0.05;
const MIN_LIGHTNESS = 0.2;
const MAX_LIGHTNESS = 0.9;

/** Two candidates closer than this in hue are the same colour at different brightness, which is
 * not a choice worth offering twice. 20° keeps the tightest genuinely distinct pair in the measured
 * set apart — red at 18° and orange at 41° — while collapsing shades of one hue. */
const MIN_HUE_SEPARATION = 20;

/** Four bits per channel: 4,096 buckets, which is coarse enough that antialiasing inside a glyph
 * lands in one bucket and fine enough to keep two similar brand colours apart. */
const QUANTISE_BITS = 4;

const MAX_CANDIDATES = 4;

export interface ContainRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** `object-contain` inside a square, as numbers.
 *
 * The same rule the sidebar already applies to the same asset (`app-sidebar.tsx`, `size-6
 * object-contain`), so an organisation's mark is framed identically in the tab and in the shell.
 * Preserve the aspect ratio, centre on the short axis, and leave the rest transparent.
 */
export function containRect(sourceWidth: number, sourceHeight: number, size: number): ContainRect {
  const scale = Math.min(size / sourceWidth, size / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  return { x: (size - width) / 2, y: (size - height) / 2, width, height };
}

function toHex(r: number, g: number, b: number): string {
  const pair = (channel: number) => channel.toString(16).padStart(2, "0");
  return `#${pair(r)}${pair(g)}${pair(b)}`;
}

/** Circular distance between two hue angles, in degrees. */
function hueGap(a: number, b: number): number {
  const raw = Math.abs(a - b) % 360;
  return raw > 180 ? 360 - raw : raw;
}

/** The colours worth offering as an organisation's brand, most-used first.
 *
 * Pure, and takes the raw RGBA array a canvas hands back, because jsdom has no canvas — so this
 * is where the rules that matter get tested, on hand-built pixel data. Frequency alone is the
 * wrong answer and the reason the filters exist: most logos are mostly white, so counting raw
 * pixels returns white for nearly every mark.
 */
export function candidateBrandColours(pixels: Uint8ClampedArray): string[] {
  const buckets = new Map<number, { r: number; g: number; b: number; count: number }>();

  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] < MIN_ALPHA) continue;
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    const shift = 8 - QUANTISE_BITS;
    const key =
      ((r >> shift) << (QUANTISE_BITS * 2)) | ((g >> shift) << QUANTISE_BITS) | (b >> shift);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.count += 1;
    } else {
      buckets.set(key, { r, g, b, count: 1 });
    }
  }

  const ranked = [...buckets.values()]
    .sort((a, b) => b.count - a.count)
    .map(({ r, g, b, count }) => ({
      hex: toHex(Math.round(r / count), Math.round(g / count), Math.round(b / count)),
      count,
    }))
    .map(({ hex }) => ({ hex, ...hexToOklch(hex) }))
    .filter(({ c, l }) => c >= MIN_CHROMA && l >= MIN_LIGHTNESS && l <= MAX_LIGHTNESS);

  const chosen: { hex: string; h: number }[] = [];
  for (const candidate of ranked) {
    if (chosen.length === MAX_CANDIDATES) break;
    if (chosen.some((taken) => hueGap(taken.h, candidate.h) < MIN_HUE_SEPARATION)) continue;
    chosen.push({ hex: candidate.hex, h: candidate.h });
  }
  return chosen.map(({ hex }) => hex);
}

/** Load a logo and draw it, contained, into a square canvas.
 *
 * `crossOrigin` is set before `src` because it has to be — set afterwards it does not apply to the
 * request, and the canvas is tainted with no error anywhere. Resolves null on anything that goes
 * wrong, including jsdom's absent 2D context.
 */
async function drawLogo(url: string, size: number): Promise<HTMLCanvasElement | null> {
  const image = await new Promise<HTMLImageElement | null>((resolve) => {
    const element = new Image();
    element.crossOrigin = "anonymous";
    element.onload = () => resolve(element);
    element.onerror = () => resolve(null);
    element.src = url;
  });
  if (!image || !image.naturalWidth || !image.naturalHeight) return null;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (!context) return null;

  const { x, y, width, height } = containRect(image.naturalWidth, image.naturalHeight, size);
  context.drawImage(image, x, y, width, height);
  return canvas;
}

/** A square PNG `data:` URI of the logo, for use as a favicon — or null if it cannot be made.
 *
 * The null case is a measured one rather than a defensive habit: POS2-1145 recorded that applying
 * the CORS policy to `foodsight-local` is still an outstanding manual step, so in local development
 * the canvas genuinely is tainted and `toDataURL` genuinely throws `SecurityError`. The caller
 * leaves the FoodSight mark in place.
 */
export async function renderLogoFavicon(url: string): Promise<string | null> {
  const canvas = await drawLogo(url, FAVICON_SIZE);
  if (!canvas) return null;
  try {
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

/** Candidate brand colours from an organisation's logo, or nothing if it has no usable colour.
 *
 * Empty is an ordinary answer, not a failure: a black-and-white mark contains no colour to offer,
 * and the editor renders no swatches for it.
 */
export async function sampleLogoColours(url: string): Promise<string[]> {
  const canvas = await drawLogo(url, SAMPLE_SIZE);
  if (!canvas) return [];
  const context = canvas.getContext("2d");
  if (!context) return [];
  try {
    return candidateBrandColours(context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data);
  } catch {
    return [];
  }
}
